"use client";

import { Text } from "@sitecore-jss/sitecore-jss-nextjs";
import React, { useCallback, useRef, useState } from "react";
import { VisuallyHidden } from "@repo/ui/visually-hidden";
import { ComponentProps } from "../../types/component-props";
import { CarouselIndicatorIcon } from "../icons";
import MediaCta, { MediaCtaFieldsProps } from "../media-cta/media-cta";
import { isMobile } from "../utils";
import { useCarouselNavigation } from "./hooks/useCarouselNavigation";
import classes from "./media-carousel.module.css";

type MediaCarouselProps = ComponentProps & {
  fields: {
    items: {
      id: string;
      fields: MediaCtaFieldsProps;
    }[];
  };
};

function MediaCarousel(props: MediaCarouselProps) {
  const items = props.fields?.items;
  const length = items?.length;
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  if (!items || items.length === 0) {
    return null;
  }

  const handleSlideIndexChange = useCallback((value: number) => {
    setCurrentSlideIndex(value);
  }, []);

  const {
    handleIndicatorClick,
    handleIndicatorKeyDown,
    handleKeyDown,
    handleIndicatorFocus,
  } = useCarouselNavigation({
    length,
    slideRefs,
    buttonRefs,
    currentSlideIndex,
    handleSlideIndexChange,
  });

  const mobileView = isMobile();

  const getTransformStyle = useCallback(() => {
    const transform = mobileView
      ? `translateY(-${currentSlideIndex * 100}%)`
      : `translateX(-${currentSlideIndex * 100}%)`;

    return {
      transform,
    };
  }, [currentSlideIndex, items.length, mobileView]);

  return (
    <div
      data-testid="carousel-root"
      className={classes.carousel}
      role="region"
      aria-roledescription="carousel"
      aria-label={"Media content carousel"}
      onKeyDown={handleKeyDown}
    >
      <div
        data-testid="slides-container"
        className={classes.carouselTrack}
        style={getTransformStyle()}
      >
        {items.map((hero, index) => (
          <div
            key={hero.id || `slide-${index}`}
            id={`slide-${index}`}
            className={classes.slide}
            role="group"
            data-index={index}
            aria-roledescription="slide"
            aria-label={`${index + 1} of ${items.length}`}
            aria-hidden={currentSlideIndex !== index}
            ref={(el) => {
              slideRefs.current[index] = el;
            }}
          >
            <MediaCta
              {...hero}
              fields={hero.fields}
              rendering={{ ...props.rendering }}
              params={props.params}
              isActive={currentSlideIndex === index}
              key={`slide-${index}-${currentSlideIndex === index}`}
            />
          </div>
        ))}
      </div>

      <div
        id="indicators-container"
        role="tablist"
        aria-label="Select a slide to display"
        className={classes.indicatorContainer}
      >
        {items.map((_, index) => (
          <button
            id={`indicator-${index}`}
            key={`indicator-${index}`}
            ref={(el) => {
              buttonRefs.current[index] = el;
            }}
            className={classes.buttonIndicator}
            aria-label={
              currentSlideIndex === index
                ? `Slide ${index + 1}`
                : `Go to slide ${index + 1}`
            }
            aria-selected={currentSlideIndex === index}
            aria-controls={`slide-${index}`}
            role="tab"
            tabIndex={0}
            type="button"
            onClick={() => handleIndicatorClick(index)}
            onKeyDown={(e) => handleIndicatorKeyDown(e, index)}
            onFocus={() => handleIndicatorFocus(index)}
          >
            <CarouselIndicatorIcon
              id={`dot-${index}`}
              isActive={currentSlideIndex === index}
              width={44}
              fontSize={44}
              aria-hidden="true"
            />
            <VisuallyHidden>
              {index + 1}
              {currentSlideIndex === index && " (Current Item)"}
            </VisuallyHidden>
          </button>
        ))}
      </div>
      {items.map((item, index) => {
        const { headline, subheading, ctaText } = item.fields;
        if (currentSlideIndex === index) {
          return (
            <VisuallyHidden
              key={item.id || index}
              id={`liveRegion-${index}`}
              aria-live="polite"
              aria-atomic="true"
            >
              {headline && <Text tag="h2" field={headline} />}
              {subheading && <Text tag="p" field={subheading} />}
              {ctaText && <Text tag="p" field={ctaText} />}
              <p>
                Item {currentSlideIndex + 1} of {items.length}
              </p>
            </VisuallyHidden>
          );
        }
        return null;
      })}
    </div>
  );
}

export default MediaCarousel;
